// Pure spreadsheet -> lead-row mapping logic for the Outreach import feature.
// Deliberately dependency-free (no React, no axios) so it can be unit
// tested directly under CRA's default Jest config, which doesn't transform
// node_modules and chokes on axios's ESM build if a test pulls it in via
// OutreachTab.jsx. Mirrors the src/lib/trayUtils.js pattern already used
// in this repo for the same reason.

// Header matching is case/punctuation-insensitive so "Google Rating", "google
// rating" and "Rating" all resolve the same way, without requiring an exact
// column-name contract from whatever scraping tool produced the file.
const FIELD_ALIASES = {
  businessName: ['name', 'businessname', 'business', 'company'],
  location:     ['area', 'location', 'town', 'city'],
  address:      ['address'],
  type:         ['type', 'category'],
  phone:        ['phone', 'tel', 'telephone', 'phonenumber'],
  email:        ['email', 'emailaddress'],
  website:      ['website', 'site', 'url'],
  facebook:     ['facebook', 'fb'],
  instagram:    ['instagram', 'ig'],
  rating:       ['googlerating', 'rating'],
  reviews:      ['reviewcount', 'reviews'],
};

export const normalizeHeader = (h) => String(h).toLowerCase().replace(/[^a-z0-9]/g, '');

// Ensures a value is a full, clickable https:// URL — spreadsheets and
// manual entry both tend to produce bare domains/handles ("example.com",
// "facebook.com/x", "@handle") rather than full URLs.
export const withProtocol = (u) => {
  const s = String(u || '').trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

export function buildSocialUrl(platform, handle) {
  const h = String(handle || '').trim();
  if (!h) return '';
  if (/^https?:\/\//i.test(h)) return h;
  switch (platform) {
    case 'instagram': return `https://instagram.com/${h.replace(/^@/, '')}`;
    case 'facebook':  return withProtocol(h.includes('facebook.com') ? h : `facebook.com/${h}`);
    case 'tiktok':    return `https://tiktok.com/@${h.replace(/^@/, '')}`;
    default:          return withProtocol(h);
  }
}

export function buildHeaderMap(headers) {
  const map = {};
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    const found = headers.find(h => aliases.includes(normalizeHeader(h)));
    if (found) map[field] = found;
  }
  return map;
}

// Turns one parsed spreadsheet row into a lead-shaped object, or null if it
// has no business name (nothing to import). Address/rating/reviews don't
// have dedicated Lead fields, so they're folded into a starter note instead
// of being dropped on the floor.
export function rowToLead(row, headerMap) {
  const get = (field) => headerMap[field] ? String(row[headerMap[field]] ?? '').trim() : '';

  const businessName = get('businessName');
  if (!businessName) return null;

  const socials = [];
  const fb = get('facebook');
  if (fb) socials.push({ platform: 'facebook', handle: fb, url: buildSocialUrl('facebook', fb) });
  const ig = get('instagram');
  if (ig) socials.push({ platform: 'instagram', handle: ig, url: buildSocialUrl('instagram', ig) });

  const location = get('location');
  const type     = get('type');

  const noteParts = [];
  const address = get('address');
  if (address) noteParts.push(`Address: ${address}`);
  const rating  = get('rating');
  const reviews = get('reviews');
  if (rating) noteParts.push(`Google rating: ${rating}${reviews ? ` (${reviews} reviews)` : ''}`);

  return {
    businessName,
    email: get('email').toLowerCase(),
    phone: get('phone'),
    website: get('website'),
    location,
    tags: [type, location].filter(Boolean),
    socials,
    importNote: noteParts.length ? `Imported from spreadsheet. ${noteParts.join(' · ')}` : '',
  };
}

// Pure pipeline from "rows parsed out of a spreadsheet" to "rows ready for
// the import preview table". A previous version of this wiring silently
// dropped every row: `raw.map(rowToLead)` passes .map()'s (row, index,
// array) straight through, so rowToLead received a row *index* as its
// headerMap argument instead of the real one, and every row's fields
// resolved to empty. Kept as one exported function specifically so that
// bug class is covered by a test rather than only caught by clicking
// through the UI.
export function parseImportRows(raw, existingLeads) {
  if (!raw.length) return { rows: [], error: 'That file has no rows' };

  const headerMap = buildHeaderMap(Object.keys(raw[0]));
  if (!headerMap.businessName) return { rows: [], error: 'Could not find a business name column in this file' };

  const existingEmails = new Set(existingLeads.map(l => (l.email || '').toLowerCase()).filter(Boolean));
  const existingNames  = new Set(existingLeads.map(l => l.businessName.trim().toLowerCase()));

  const rows = raw
    .map(row => rowToLead(row, headerMap))
    .filter(Boolean)
    .map(r => ({
      ...r,
      duplicate: r.email ? existingEmails.has(r.email) : existingNames.has(r.businessName.trim().toLowerCase()),
    }))
    .map(r => ({ ...r, include: !r.duplicate }));

  return { rows, error: null };
}
